import Image from "next/image";
import Link from "next/link";
import useSWR from "swr";

import { useMemo, useState } from "react";

import addDays from "date-fns/addDays";
import format from "date-fns/format";
import sortBy from "lodash/sortBy";
import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import useAuthentication from "hooks/useAuthentication";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

import PriceAssessmentModal, {
  renderNumber,
} from "../HelixtapPriceAssessmentsPage/PriceAssessmentModal";

const FobDailyWidget = ({}: {}) => {
  const { account: loggedInAccount } = useAuthentication();
  const helixtapPermissions = loggedInAccount?.helixtapPermissions || {};
  const hasPermission = helixtapPermissions.commentary;

  const [showModal, setShowModal] = useState<any>(undefined);

  const { data } = useSWR(
    () => {
      const endpoint = new ApiEndpoint("/api/v3/helixtap/price-assessments");
      endpoint.searchParams.append(
        "from",
        format(addDays(new Date(), -7), "dd-MM-yyyy")
      );
      endpoint.searchParams.append("to", format(new Date(), "dd-MM-yyyy"));
      return endpoint.href;
    },
    baseFetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    }
  );

  const filteredData = useMemo(() => {
    if (!data?.fob_data) return [];

    return sortBy(data.fob_data, ["date"])
      .filter((i) => i.comment)
      .reverse()
      .slice(0, 5);
  }, [data]);

  if (!hasPermission || !filteredData?.length) return null;

  return (
    <CustomCard className="flex-1 p-[15px]">
      <div>
        <div
          className={twMerge(
            "pb-[10px]",
            "font-medium",
            "text-[16px]",
            "text-active-black",
            "flex"
          )}
        >
          <div className="flex-1 flex items-center space-x-[5px]">
            <Image
              alt="Helixtap"
              src={require("../../../assets/helixtap.svg")}
              width={10}
              height={15}
            />
            <span>Helixtap Commentary</span>
          </div>
          <div className="">
            <Link
              href={`/helixtap/price-assessments`}
              className={twMerge(
                "text-[14px]",
                "font-medium",
                "text-smart-teal"
              )}
            >
              View More »
            </Link>
          </div>
        </div>
        <div className="text-[14px]">
          <div
            className={twMerge(
              "flex justify-between",
              "pb-[10px]",
              "font-bold",
              "mt-[15px]"
            )}
          >
            <div>Date</div>
            <div>Commentary</div>
          </div>
          {filteredData.map((record: any) => (
            <div
              key={record.id}
              className={twMerge(
                "flex justify-between",
                "py-[9px]",
                "border-0 border-t border-solid border-inactive-gray"
              )}
            >
              <div>{format(new Date(record.date), "dd-MM-yyyy")}</div>
              <div>
                {record.comment ? (
                  <div
                    className={twMerge(
                      "cursor-pointer",
                      "font-medium",
                      "text-center"
                    )}
                    onClick={() => setShowModal(record)}
                  >
                    Read »
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <PriceAssessmentModal
            priceAssessment={showModal}
            onClose={(updated) => {
              setShowModal(undefined);
            }}
            topContent={
              <div className="flex space-x-[30px] justify-evenly">
                <div
                  className={twMerge(
                    "border-0 border-l-[10px] border-solid",
                    "pl-[10px]",
                    showModal.SIR20.differential > 0
                      ? "border-bullish-green/50"
                      : showModal.SIR20.differential === 0
                      ? "border-active-black/50"
                      : "border-bearish-red/50"
                  )}
                >
                  <div className="font-bold">SIR20</div>
                  <div>
                    {renderNumber(showModal.SIR20.price, showModal.SIR20)}
                  </div>
                </div>
                <div
                  className={twMerge(
                    "border-0 border-l-[10px] border-solid",
                    "pl-[10px]",
                    showModal.STR20.differential > 0
                      ? "border-bullish-green/50"
                      : showModal.STR20.differential === 0
                      ? "border-active-black/50"
                      : "border-bearish-red/50"
                  )}
                >
                  <div className="font-bold">STR20</div>
                  <div>
                    {renderNumber(showModal.STR20.price, showModal.STR20)}
                  </div>
                </div>
                <div
                  className={twMerge(
                    "border-0 border-l-[10px] border-solid",
                    "pl-[10px]",
                    showModal["AFR10 (Implied)"].differential > 0
                      ? "border-bullish-green/50"
                      : showModal["AFR10 (Implied)"].differential === 0
                      ? "border-active-black/50"
                      : "border-bearish-red/50"
                  )}
                >
                  <div className="font-bold">AFR10</div>
                  <div>
                    {renderNumber(
                      showModal["AFR10 (Implied)"].price,
                      showModal["AFR10 (Implied)"]
                    )}
                  </div>
                </div>
                <div
                  className={twMerge(
                    "border-0 border-l-[10px] border-solid",
                    "pl-[10px]",
                    showModal.Sentiment.differential > 0
                      ? "border-bullish-green/50"
                      : showModal.Sentiment.differential === 0
                      ? "border-active-black/50"
                      : "border-bearish-red/50"
                  )}
                >
                  <div className="font-bold">Sentiment Index</div>
                  <div>
                    {renderNumber(
                      showModal.Sentiment.index_number,
                      showModal.Sentiment
                    )}
                  </div>
                </div>
              </div>
            }
          />
        )}
      </div>
    </CustomCard>
  );
};
export default FobDailyWidget;
